import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Carrier, carriersApi } from "../../../services/carriersApi";

export default function Carriers() {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Carrier | null>(null);
  const [name, setName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result: any = await carriersApi.list(search, page);
    setLoading(false);
    if (result.success) {
      setCarriers(result.data.data ?? []);
      setLastPage(result.data.last_page ?? 1);
    } else {
      setError(result.data?.message ?? "Could not load carriers.");
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setError(null);
    setShowForm(true);
  };

  const openEdit = (carrier: Carrier) => {
    setEditing(carrier);
    setName(carrier.name);
    setError(null);
    setShowForm(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Carrier name is required.");
      return;
    }

    setSaving(true);
    const result: any = editing
      ? await carriersApi.update(editing.id, name.trim())
      : await carriersApi.create(name.trim());
    setSaving(false);

    if (!result.success) {
      setError(result.data?.message ?? "Could not save the carrier.");
      return;
    }

    setShowForm(false);
    setMessage(editing ? "Carrier updated." : "Carrier added.");
    setTimeout(() => setMessage(null), 3000);
    await load();
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Carriers</h1>
              <p className="mt-1 text-sm text-slate-500">Manage the carrier names available on shipment reports.</p>
            </div>
            <button type="button" onClick={openCreate} className="inline-flex items-center justify-center gap-2 rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white">
              <PlusIcon className="h-5 w-5" /> Add Carrier
            </button>
          </div>

          {message ? <div className="mt-4 rounded-md bg-emerald-50 p-3 text-sm font-medium text-emerald-800">{message}</div> : null}

          <form className="mt-5 flex gap-2" onSubmit={(event) => { event.preventDefault(); setPage(1); setSearch(searchInput.trim()); }}>
            <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search carriers" className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <button type="submit" className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700">Search</button>
          </form>

          <div className="mt-5 overflow-hidden rounded-lg border border-slate-200">
            {loading ? <div className="p-6 text-sm text-slate-500">Loading carriers...</div> : carriers.length === 0 ? <div className="p-6 text-sm text-slate-500">No carriers found.</div> : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50"><tr><th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Name</th><th className="px-4 py-3"><span className="sr-only">Actions</span></th></tr></thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {carriers.map((carrier) => <tr key={carrier.id}><td className="px-4 py-3 text-sm font-medium text-slate-900">{carrier.name}</td><td className="px-4 py-3 text-right"><button type="button" onClick={() => openEdit(carrier)} className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"><PencilSquareIcon className="h-4 w-4" /> Edit</button></td></tr>)}
                </tbody>
              </table>
            )}
          </div>

          {lastPage > 1 ? <div className="mt-4 flex items-center justify-between"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1.5 text-sm disabled:opacity-40">Previous</button><span className="text-sm text-slate-500">Page {page} of {lastPage}</span><button type="button" disabled={page >= lastPage} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1.5 text-sm disabled:opacity-40">Next</button></div> : null}
        </div>
      </div>

      {showForm ? <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4"><form onSubmit={submit} className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"><h2 className="text-lg font-semibold text-slate-900">{editing ? "Edit Carrier" : "Add Carrier"}</h2><label className="mt-4 block text-sm font-medium text-slate-700">Carrier Name<input autoFocus value={name} onChange={(event) => setName(event.target.value)} maxLength={255} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2" /></label>{error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}<div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setShowForm(false)} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold">Cancel</button><button type="submit" disabled={saving} className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? "Saving..." : "Save"}</button></div></form></div> : null}
    </div>
  );
}
